import {mapObject, objectFromEntries} from '@benchling/js-utils';
import {produce} from 'immer';
import React, {useState} from 'react';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {PolymerSubtype} from 'constants/MolbioConstants';
import {
  BulkAssemblyFragmentBin,
  BulkAssemblyMethod,
  TableFragment,
  FragmentUpdate,
  MethodData,
} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {ValidationStatus} from 'molbio/bulk-assembly/BulkAssemblyValidationConstants';
import {
  BA_EMPTY_VALUE,
  baId,
  BAIdValue,
  baNumericId,
  BANumericIdValue,
  baRawText,
  BARawTextValue,
  BULK_ASSEMBLY_ORIENTATION_NAME,
  BulkAssemblyOrientation,
} from 'molbio/bulk-assembly/BulkAssemblyValues';
import BulkAssemblyFragmentsStructuredTableAdapter, {
  BulkAssemblyFragmentsStructuredTable,
  BulkAssemblyFragmentsStructuredTableMetadata,
  BulkAssemblyFragmentsStructuredTableRowMetadata,
} from 'molbio/bulk-assembly/tables/BulkAssemblyFragmentsStructuredTableAdapter';
import {
  formatValidationMessages,
  getCellText,
  maybeMakeCellError,
} from 'molbio/bulk-assembly/tables/BulkAssemblyTableUtil';
import {NO_ENZYME_SENTINEL} from 'molbio/enzymes/EnzymeConstants';
import {getEnzyme, getEnzymeByName, isDoubleCutterEnzyme} from 'molbio/enzymes/EnzymeUtil';
import getColumnSortingContextMenuOptions from 'notebook/structured-tables/column-sorting/getColumnSortingMenuOptions';
import {StructuredTableColumn, StructuredTableRow} from 'notebook/structured-tables/StructuredTableConstants';
import {StructuredTableOutsideNotebookWithTableViews} from 'notebook/structured-tables/StructuredTableOutsideNotebook';
import {makeStructuredTableColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {EMPTY_PLACEHOLDER_INSERTION_CONTEXT} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {BaseTypedValueType, Cell, TableColumnType} from 'notebook/tables/TableConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import sortedBy from 'util/sortedBy';

enum ColumnKey {
  POLYMER = 'POLYMER',
  BIN = 'BIN',
  START = 'START',
  END = 'END',
  LENGTH = 'LENGTH',
  ORIENTATION = 'ORIENTATION',
  TYPE_IIS_ENZYME = 'TYPE_IIS_ENZYME',
  FIVE_PRIME_RESTRICTION_ENZYME = 'FIVE_PRIME_RESTRICTION_ENZYME',
  THREE_PRIME_RESTRICTION_ENZYME = 'THREE_PRIME_RESTRICTION_ENZYME',
  FRAGMENT_PRODUCTION_METHOD = 'FRAGMENT_PRODUCTION_METHOD',
  PREFERRED_FIVE_PRIMER = 'PREFERRED_FIVE_PRIMER',
  PREFERRED_THREE_PRIMER = 'PREFERRED_THREE_PRIMER',
  STATUS = 'STATUS',
}

const COLUMN_ORDER = Object.values(ColumnKey);

interface ColumnInfo {
  columnKey: ColumnKey;
  structuredTableColumn: StructuredTableColumn;
}

function getMethodDependentColumns(
  bulkAssemblyMethodData: MethodData,
  canUsePrimers: boolean,
  columnWidths: {[key: string]: number}
): Array<ColumnInfo> {
  const endpointColumns = [
    {
      columnKey: ColumnKey.START,
      structuredTableColumn: {
        ...makeStructuredTableColumn({
          name: 'Start',
          columnType: TableColumnType.BULK_ASSEMBLY_FRAGMENT_ENDPOINT,
          width: columnWidths[ColumnKey.START] ?? 80,
        }),
        columnType: TableColumnType.BULK_ASSEMBLY_FRAGMENT_ENDPOINT,
        side: 'start',
      },
    },
    {
      columnKey: ColumnKey.END,
      structuredTableColumn: {
        ...makeStructuredTableColumn({
          name: 'End',
          columnType: TableColumnType.BULK_ASSEMBLY_FRAGMENT_ENDPOINT,
          width: columnWidths[ColumnKey.END] ?? 80,
        }),
        columnType: TableColumnType.BULK_ASSEMBLY_FRAGMENT_ENDPOINT,
        side: 'end',
      },
    },
  ];

  const preferredPrimerColumns = [
    {
      columnKey: ColumnKey.PREFERRED_FIVE_PRIMER,
      structuredTableColumn: {
        ...makeStructuredTableColumn({
          name: "Preferred 5' primer",
          columnType: TableColumnType.BULK_ASSEMBLY_PREFERRED_PRIMER,
          width: columnWidths[ColumnKey.PREFERRED_FIVE_PRIMER] ?? 135,
        }),
        columnType: TableColumnType.BULK_ASSEMBLY_PREFERRED_PRIMER,
      },
    },
    {
      columnKey: ColumnKey.PREFERRED_THREE_PRIMER,
      structuredTableColumn: {
        ...makeStructuredTableColumn({
          name: "Preferred 3' primer",
          columnType: TableColumnType.BULK_ASSEMBLY_PREFERRED_PRIMER,
          width: columnWidths[ColumnKey.PREFERRED_THREE_PRIMER] ?? 135,
        }),
        columnType: TableColumnType.BULK_ASSEMBLY_PREFERRED_PRIMER,
      },
    },
  ];
  const fragmentProductionMethodColumn = {
    columnKey: ColumnKey.FRAGMENT_PRODUCTION_METHOD,
    structuredTableColumn: makeStructuredTableColumn({
      name: 'Fragment production method',
      columnType: TableColumnType.STRING,
      width: columnWidths[ColumnKey.FRAGMENT_PRODUCTION_METHOD] ?? 220,
      isReadOnly: true,
    }),
  };

  const orientationColumn = {
    columnKey: ColumnKey.ORIENTATION,
    structuredTableColumn: {
      ...makeStructuredTableColumn({
        name: 'Orientation',
        columnType: TableColumnType.OPTION_SELECTOR,
        width: columnWidths[ColumnKey.ORIENTATION],
      }),
      columnType: TableColumnType.OPTION_SELECTOR,
      options: Object.values(BULK_ASSEMBLY_ORIENTATION_NAME),
    },
  };

  const restrictionEnzymeColumns = [
    {
      columnKey: ColumnKey.FIVE_PRIME_RESTRICTION_ENZYME,
      structuredTableColumn: makeStructuredTableColumn({
        name: "5' enzyme",
        columnType: TableColumnType.BULK_ASSEMBLY_RESTRICTION_ENZYME,
        width: columnWidths[ColumnKey.FIVE_PRIME_RESTRICTION_ENZYME] ?? 135,
        isReadOnly: false,
      }),
    },
    {
      columnKey: ColumnKey.THREE_PRIME_RESTRICTION_ENZYME,
      structuredTableColumn: makeStructuredTableColumn({
        name: "3' enzyme",
        columnType: TableColumnType.BULK_ASSEMBLY_RESTRICTION_ENZYME,
        width: columnWidths[ColumnKey.THREE_PRIME_RESTRICTION_ENZYME] ?? 135,
        isReadOnly: false,
      }),
    },
  ];

  switch (bulkAssemblyMethodData.type) {
    case BulkAssemblyMethod.GOLDEN_GATE:
      return [
        ...endpointColumns,
        {
          columnKey: ColumnKey.TYPE_IIS_ENZYME,
          structuredTableColumn: makeStructuredTableColumn({
            name: 'Type IIS enzyme',
            columnType: TableColumnType.STRING,
            width: columnWidths[ColumnKey.TYPE_IIS_ENZYME] ?? 135,
            isReadOnly: true,
          }),
        },
        fragmentProductionMethodColumn,
        ...(canUsePrimers ? preferredPrimerColumns : []),
        orientationColumn,
      ];
    case BulkAssemblyMethod.GIBSON:
      return [
        ...endpointColumns,
        fragmentProductionMethodColumn,
        ...restrictionEnzymeColumns,
        ...(canUsePrimers ? preferredPrimerColumns : []),
        orientationColumn,
      ];
    case BulkAssemblyMethod.CONCATENATION:
      if (bulkAssemblyMethodData.parameters.polymerSubtype === PolymerSubtype.dna_sequence) {
        // Only include orientation column when the construct type is double-stranded; otherwise all fragments
        // will be single-stranded and we should hide the orientation.
        return [...endpointColumns, orientationColumn];
      } else {
        return endpointColumns;
      }
    default:
      return [orientationColumn];
  }
}

function getColumns(
  bulkAssemblyMethodData: MethodData,
  bins: Array<BulkAssemblyFragmentBin>,
  canUsePrimers: boolean,
  columnWidths: {[key: string]: number},
  supportedPolymerTypes: Array<PolymerSubtype>
): Array<ColumnInfo> {
  let bioentityTypeFilters: Array<string>;
  if (supportedPolymerTypes.length === 1) {
    if (supportedPolymerTypes[0] === PolymerSubtype.dna_oligo) {
      bioentityTypeFilters = [ModelTypeConstants.DNA_OLIGO];
    } else if (supportedPolymerTypes[0] === PolymerSubtype.rna_oligo) {
      bioentityTypeFilters = [ModelTypeConstants.RNA_OLIGO];
    } else if (supportedPolymerTypes[0] === PolymerSubtype.aa_sequence) {
      bioentityTypeFilters = [ModelTypeConstants.PROTEIN];
    } else if (supportedPolymerTypes[0] === PolymerSubtype.rna_sequence) {
      bioentityTypeFilters = [ModelTypeConstants.RNA_SEQUENCE];
    } else {
      bioentityTypeFilters = [ModelTypeConstants.SEQUENCE];
    }
    // This logic encodes some assumptions about supported polymer types:
    // There is only ever 1 or 2 supported type, and if 2, then it is always one
    // long sequence type and one short of the same nucleotide type
  } else if (supportedPolymerTypes.includes(PolymerSubtype.dna_oligo)) {
    bioentityTypeFilters = [ModelTypeConstants.SEQUENCE, ModelTypeConstants.DNA_OLIGO];
  } else {
    bioentityTypeFilters = [ModelTypeConstants.RNA_SEQUENCE, ModelTypeConstants.RNA_OLIGO];
  }

  const columns = [
    {
      columnKey: ColumnKey.POLYMER,
      structuredTableColumn: {
        ...makeStructuredTableColumn({
          name: 'Sequence',
          columnType: TableColumnType.BULK_ASSEMBLY_FRAGMENT_POLYMER,
          width: columnWidths[ColumnKey.POLYMER] ?? 135,
        }),
        columnType: TableColumnType.BULK_ASSEMBLY_FRAGMENT_POLYMER,
        bioentityTypeFilters,
      },
    },
    {
      columnKey: ColumnKey.BIN,
      structuredTableColumn: {
        ...makeStructuredTableColumn({
          name: 'Bin',
          columnType: TableColumnType.BULK_ASSEMBLY_BIN_SELECTOR,
          width: columnWidths[ColumnKey.BIN] ?? 135,
        }),
        columnType: TableColumnType.BULK_ASSEMBLY_BIN_SELECTOR,
        bins,
      },
    },
    {
      columnKey: ColumnKey.LENGTH,
      structuredTableColumn: makeStructuredTableColumn({
        name: 'Length',
        columnType: TableColumnType.BULK_ASSEMBLY_LENGTH,
        isReadOnly: true,
        width: columnWidths[ColumnKey.LENGTH],
      }),
    },
    {
      columnKey: ColumnKey.STATUS,
      structuredTableColumn: makeStructuredTableColumn({
        name: 'Status',
        columnType: TableColumnType.BULK_ASSEMBLY_VALIDATION_STATUS,
        isReadOnly: true,
        width: columnWidths[ColumnKey.STATUS] ?? 300,
      }),
    },
    ...getMethodDependentColumns(bulkAssemblyMethodData, canUsePrimers, columnWidths),
  ];

  return sortedBy(columns, [(column) => COLUMN_ORDER.indexOf(column.columnKey)]);
}

function prepareBioentityCell(polymer: {text: string; id?: string; error?: string; locked?: boolean}): Cell {
  if (polymer.id === undefined) {
    return {text: polymer.text, error: maybeMakeCellError(polymer.error), locked: polymer.locked};
  } else {
    return {
      text: '',
      typedValue: {
        type: BaseTypedValueType.BIOENTITY,
        bioentitySummary: {id: polymer.id, name: polymer.text},
      },
    };
  }
}

function parseBioentityCell(bioentityCell: Cell): BAIdValue | BARawTextValue {
  if (bioentityCell.typedValue?.type === BaseTypedValueType.BIOENTITY) {
    return baId(bioentityCell.typedValue.bioentitySummary.id);
  } else {
    return baRawText(bioentityCell.text);
  }
}

function parseRestrictionEnzymeCell(restrictionEnzymeCell: Cell): BANumericIdValue | BARawTextValue {
  let maybeEnzyme = getEnzymeByName(restrictionEnzymeCell.text);
  if (maybeEnzyme != null) {
    return baNumericId(maybeEnzyme.id);
  }
  const cellValue = parseInt(
    restrictionEnzymeCell.typedValue?.type === BaseTypedValueType.STRING
      ? restrictionEnzymeCell.typedValue.rawValue
      : restrictionEnzymeCell.text,
    10
  );
  maybeEnzyme = getEnzyme(cellValue);
  if (cellValue === NO_ENZYME_SENTINEL || (maybeEnzyme != null && !isDoubleCutterEnzyme(maybeEnzyme))) {
    return baNumericId(cellValue);
  }
  return baRawText(restrictionEnzymeCell.text);
}

export interface BulkAssemblyFragmentsTableProps {
  assemblyId: string;
  bulkAssemblyMethodData: MethodData;
  isFilterDisabledByStatus: {[key in ValidationStatus]: boolean};
  fragments: Array<TableFragment>;
  bins: Array<BulkAssemblyFragmentBin>;
  isReadOnly: boolean;
  canUsePrimers: boolean;
  onChangeFragments: (fragments: Array<FragmentUpdate>) => void;
  onReorderFragments: (reorderedFragmentCids: Array<string>) => void;
  onViewFragments: (fragmentCids: Array<string>) => void;
  onDeleteAll: () => void;
  onDeleteFiltered: () => void;
  onDeleteRange: (startIndex: number, endIndex: number) => void;
  statusFilter: ValidationStatus | null;
  onSetStatusFilter: (newStatusFilter: ValidationStatus | null) => void;
  canSpecifyEndpoints: boolean;
  supportedPolymerTypes: Array<PolymerSubtype>;
  icon: BenchlingIconName;
}

export default function BulkAssemblyFragmentsTable({
  assemblyId,
  bulkAssemblyMethodData,
  fragments,
  bins,
  isFilterDisabledByStatus,
  isReadOnly,
  canUsePrimers,
  onChangeFragments,
  onReorderFragments,
  onViewFragments,
  onDeleteAll,
  onDeleteFiltered,
  onDeleteRange,
  canSpecifyEndpoints,
  statusFilter,
  onSetStatusFilter,
  supportedPolymerTypes,
  icon,
}: BulkAssemblyFragmentsTableProps): JSX.Element {
  const tableCid = `${assemblyId}-fragmentsTable`;
  const adapter = BulkAssemblyFragmentsStructuredTableAdapter.getAdapter();

  const [columnWidths, setColumnWidths] = useState<{[key: string]: number}>({});
  const columns: Array<ColumnInfo> = getColumns(
    bulkAssemblyMethodData,
    bins,
    canUsePrimers,
    columnWidths,
    supportedPolymerTypes
  );
  const columnIndexByKey = objectFromEntries(columns.map((column, i) => [column.columnKey, i]));
  const tableMetadata: BulkAssemblyFragmentsStructuredTableMetadata = {
    columns: columns.map((column) => column.structuredTableColumn),
    onViewFragments,
    isFilterDisabledByStatus,
    isFiltered: statusFilter != null,
    isReadOnly,
    onDeleteAll,
    onDeleteFiltered,
    onDeleteRange,
    statusFilter,
    onSetStatusFilter,
    tableRowsName: 'fragments',
  };

  const fragmentsTable = adapter.createTable({
    // "name" is the table subtitle, which we omit. The actual title is defined in the adapter.
    name: '',
    metadata: tableMetadata,
    entryId: null,
    schemas: [],
    selectorsById: {},
    structuredTableInsertionContext: EMPTY_PLACEHOLDER_INSERTION_CONTEXT,
  });
  // The provenance UUID must stay consistent between renders, so use the tableCid
  fragmentsTable.provenanceUuid = tableCid;
  function buildStructuredTableRows(): Array<
    StructuredTableRow<BulkAssemblyFragmentsStructuredTableRowMetadata>
  > {
    return fragments.map((fragment, rowIndex) => {
      const row = adapter.getBlankRow(tableMetadata, rowIndex, fragmentsTable.columns, {
        fragmentCid: fragment.cid,
        polymerVersionId: fragment.polymerVersionId,
        enzymeCutsiteData5: fragment.enzymeCutsiteData5,
        enzymeCutsiteData3: fragment.enzymeCutsiteData3,
      });

      return produce(row, (draft) => {
        draft.cells[columnIndexByKey[ColumnKey.POLYMER]] = prepareBioentityCell(fragment.polymer);
        draft.cells[columnIndexByKey[ColumnKey.BIN]] = {
          text: fragment.bin.text,
          error: maybeMakeCellError(fragment.bin.error),
        };
        if (columnIndexByKey.hasOwnProperty(ColumnKey.START)) {
          draft.cells[columnIndexByKey[ColumnKey.START]] = {
            text: fragment.start.text,
            locked: fragment.start.locked,
            error: maybeMakeCellError(fragment.start.error),
          };
        }
        if (columnIndexByKey.hasOwnProperty(ColumnKey.END)) {
          draft.cells[columnIndexByKey[ColumnKey.END]] = {
            text: fragment.end.text,
            locked: fragment.end.locked,
            error: maybeMakeCellError(fragment.end.error),
          };
        }
        draft.cells[columnIndexByKey[ColumnKey.LENGTH]] = {
          text: fragment.length.text,
        };
        if (columnIndexByKey.hasOwnProperty(ColumnKey.ORIENTATION)) {
          draft.cells[columnIndexByKey[ColumnKey.ORIENTATION]] = {
            text: fragment.orientation.text,
            locked: fragment.orientation.locked,
            error: maybeMakeCellError(fragment.orientation.error),
          };
        }
        if (columnIndexByKey.hasOwnProperty(ColumnKey.TYPE_IIS_ENZYME)) {
          draft.cells[columnIndexByKey[ColumnKey.TYPE_IIS_ENZYME]] = {
            text:
              bulkAssemblyMethodData.type === BulkAssemblyMethod.GOLDEN_GATE
                ? getEnzyme(bulkAssemblyMethodData.parameters.defaultRestrictionEnzymeId).name
                : 'No enzyme selected',
            locked: fragment.restrictionEnzyme5.locked || fragment.restrictionEnzyme3.locked,
          };
        }
        if (columnIndexByKey.hasOwnProperty(ColumnKey.FIVE_PRIME_RESTRICTION_ENZYME)) {
          draft.cells[columnIndexByKey[ColumnKey.FIVE_PRIME_RESTRICTION_ENZYME]] = {
            text: fragment.restrictionEnzyme5.text,
            locked: fragment.restrictionEnzyme5.locked,
          };
        }
        if (columnIndexByKey.hasOwnProperty(ColumnKey.THREE_PRIME_RESTRICTION_ENZYME)) {
          draft.cells[columnIndexByKey[ColumnKey.THREE_PRIME_RESTRICTION_ENZYME]] = {
            text: fragment.restrictionEnzyme3.text,
            locked: fragment.restrictionEnzyme3.locked,
          };
        }
        if (columnIndexByKey.hasOwnProperty(ColumnKey.FRAGMENT_PRODUCTION_METHOD)) {
          draft.cells[columnIndexByKey[ColumnKey.FRAGMENT_PRODUCTION_METHOD]] = {
            text: fragment.fragmentProductionMethod?.text ?? '',
          };
        }
        if (canUsePrimers) {
          draft.cells[columnIndexByKey[ColumnKey.PREFERRED_FIVE_PRIMER]] = prepareBioentityCell(
            fragment.preferredPrimer5
          );
          draft.cells[columnIndexByKey[ColumnKey.PREFERRED_THREE_PRIMER]] = prepareBioentityCell(
            fragment.preferredPrimer3
          );
        }
        draft.cells[columnIndexByKey[ColumnKey.STATUS]] = {
          text: formatValidationMessages(fragment.validationMessages, isReadOnly),
        };
      });
    });
  }
  fragmentsTable.rows = buildStructuredTableRows();

  function handleFragmentsTableChange(newFragmentsTable: BulkAssemblyFragmentsStructuredTable): void {
    setColumnWidths(mapObject(columnIndexByKey, (index) => newFragmentsTable.columns[index].width));
    onChangeFragments(
      newFragmentsTable.rows.map((row) => ({
        cid: row.metadata.fragmentCid,
        polymer: parseBioentityCell(row.cells[columnIndexByKey[ColumnKey.POLYMER]]),
        polymerVersionDbId: row.metadata.polymerVersionId,
        bin: baRawText(getCellText(row.cells[columnIndexByKey[ColumnKey.BIN]])),
        start: canSpecifyEndpoints
          ? baRawText(getCellText(row.cells[columnIndexByKey[ColumnKey.START]]))
          : BA_EMPTY_VALUE,
        end: canSpecifyEndpoints
          ? baRawText(getCellText(row.cells[columnIndexByKey[ColumnKey.END]]))
          : BA_EMPTY_VALUE,
        orientation: columnIndexByKey.hasOwnProperty(ColumnKey.ORIENTATION)
          ? baRawText(getCellText(row.cells[columnIndexByKey[ColumnKey.ORIENTATION]]))
          : baRawText(BulkAssemblyOrientation.FORWARD),
        preferredPrimer5: canUsePrimers
          ? parseBioentityCell(row.cells[columnIndexByKey[ColumnKey.PREFERRED_FIVE_PRIMER]])
          : BA_EMPTY_VALUE,
        preferredPrimer3: canUsePrimers
          ? parseBioentityCell(row.cells[columnIndexByKey[ColumnKey.PREFERRED_THREE_PRIMER]])
          : BA_EMPTY_VALUE,
        restrictionEnzyme5: columnIndexByKey.hasOwnProperty(ColumnKey.FIVE_PRIME_RESTRICTION_ENZYME)
          ? parseRestrictionEnzymeCell(row.cells[columnIndexByKey[ColumnKey.FIVE_PRIME_RESTRICTION_ENZYME]])
          : row.metadata.enzymeCutsiteData5?.restrictionEnzymeId != null
            ? baNumericId(row.metadata.enzymeCutsiteData5.restrictionEnzymeId)
            : BA_EMPTY_VALUE,
        restrictionEnzyme3: columnIndexByKey.hasOwnProperty(ColumnKey.FIVE_PRIME_RESTRICTION_ENZYME)
          ? parseRestrictionEnzymeCell(row.cells[columnIndexByKey[ColumnKey.THREE_PRIME_RESTRICTION_ENZYME]])
          : row.metadata.enzymeCutsiteData3?.restrictionEnzymeId != null
            ? baNumericId(row.metadata.enzymeCutsiteData3.restrictionEnzymeId)
            : BA_EMPTY_VALUE,
      }))
    );
  }
  const structuredTable = {...fragmentsTable, cid: tableCid};

  return (
    <div {...getTestProps({component: 'BulkAssemblyFragmentsTable', element: 'root'})}>
      <StructuredTableOutsideNotebookWithTableViews
        structuredTable={structuredTable}
        cid={tableCid}
        updateHandler={handleFragmentsTableChange}
        includeUnsubmittedStatus={false}
        hideColumnHeaderIcons={true}
        locked={isReadOnly}
        iconOverride={icon}
        canRename={false}
        isFixedHeight={true}
        getExtraColumnContextMenuOptionsForStructuredTable={(selectionRange, table, updateColumnConstraint) =>
          getColumnSortingContextMenuOptions(
            selectionRange,
            null,
            table,
            undefined,
            structuredTable,
            isReadOnly,
            (sortedRowIndices) => {
              const fragmentCids = sortedRowIndices.map(
                (index) => fragmentsTable.rows[index].metadata.fragmentCid
              );
              assert(!fragmentCids.includes(null), 'Invalid row indices received');
              onReorderFragments(fragmentCids as Array<string>);
            },
            null
          )
        }
      />
    </div>
  );
}
